import React, {useLayoutEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import ReactHtmlParser from 'react-html-parser';
import {combineClassNames, getMediaPath, interpolate, reactParserCommonOptions, resolvePopupCallback} from '../../../../app/functions';
import * as Actions from '../../../../redux/actions';
import {
    setIsConsentsUpdateSuccessful,
    setIsMarketingApproved,
    setIsUpdatedConsents,
    updateUserData,
    updateUserDataSharingAndMarketingConsent,
    updateUserDataSharingConsent
} from '../../../../redux/actions';
import closeIconPath from '../../../../assets/icons/x-close-blue-icon.svg';
import checkedCheckboxImagePath from '../../../../assets/icons/cb_checked.svg';
import emptyCheckboxImagePath from '../../../../assets/icons/cb_empty.svg';
import './index.scss';
import * as Popups from "../../../../constants/popup-types";
import HtmlCheckbox from "../../../../components/forms/htmlCheckbox";
import {useDeviceState} from "../../../../app/device_state";

const SOURCE_OF_APPROVAL = 'Website Popup';
const CLOSE_TIMEOUT = 250;

const countTrueBooleans = (...vars) => { // Using rest parameter for flexible number of inputs
    let count = 0;
    for (const v of vars) {
        if (v === true) {
            count++;
        }
    }
    return count;
}

const determineContent = (options, marketingRequired, dataSharingRequired, subsidyRequired, configData) => {
    let content = {}

    if (marketingRequired) {
        content = {...content, ...(options.marketing)}
    }

    if (subsidyRequired) {
        content = {...content, ...(options.subsidy)}
    }
    if (dataSharingRequired) {
        content = {...content, ...(options.dataSharing)}
        content.description = content.description || configData.description_content
    }
    if (countTrueBooleans(marketingRequired, dataSharingRequired, subsidyRequired) > 1) {
        content = {...content, ...(options.all)}
    }

    content.title = content.title || configData.title
    content.subtitle = content.subtitle || configData.subtitle
    return content
}


const UsersConsentsPopup = ({
                                onAgreement,
                                handleClose,
                                isShowMarketingCheckbox,
                                isShowDataSharingCheckbox = true,
                                isShowSubsidyCheckbox,
                                benefitId
                            }) => {

    const dispatch = useDispatch();

    const userData = useSelector((store) => store.config.UserData || {});
    const configData = useSelector((store) => store.config.dataSharingConfig);
    const translations = useSelector((store) => store.config.Translations);
    const consentsConfig = useSelector((store) => store.config.Parameters.consentsConfig);

    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const {isMobile} = useDeviceState()
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDataSharingCheckboxChecked, setIsDataSharingCheckboxChecked] = useState(false);
    const [isApprovedMarketing, setIsApprovedMarketing] = useState(false);
    const [hasDataCheckboxError, setIsShowCheckboxError] = useState(false);
    const [isShowMarketingCheckboxError, setIsShowMarketingCheckboxError] = useState(false);
    const [isApprovedSubsidy, setIsApprovedSubsidy] = useState(false);
    const [isShowSubsidyCheckboxError, setIsShowSubsidyCheckboxError] = useState(false);
    const [isClickedSubmit, setIsClickedSubmit] = useState(false);

    const [linkDataClicked, setLinkDataClicked]  = useState(false);
    const [linkMarketingClicked, setLinkMarketingClicked]  = useState(false);
    const [linkSubsidyClicked, setLinkSubsidyClicked]  = useState(false);

    const [showDataLinkNotClickedError, setShowDataLinkNotClickedError] = useState(false);
    const [showMarketingLinkNotClickedError, setShowMarketingLinkNotClickedError] = useState(false);
    const [showSubsidyLinkNotClickedError, setShowSubsidyLinkNotClickedError] = useState(false);


    const content = determineContent(
        consentsConfig,
        isShowMarketingCheckbox,
        isShowDataSharingCheckbox,
        isShowSubsidyCheckbox,
        configData);

    const headerText = interpolate(content.title, userData)
    const subtitle = interpolate(content.subtitle, userData)
    const descriptionText = interpolate(content.description, userData)
    const marketingCheckboxHtml = interpolate(content.marketingCheckbox || translations.marketing_consent_text_html, userData)
    const dataSharingCheckboxHtml = interpolate(content.dataSharingCheckbox || translations.data_agreement_checkbox_html, userData)
    const subsidyCheckboxHtml = interpolate(content.subsidyCheckbox || translations.subsidy_checkbox_html, userData)

    const listItems = [
        {
            image: configData?.first_media_path,
            text: configData?.first_media_text,
            subText: configData?.first_media_sub_text,
        },
        {
            image: configData?.second_media_path,
            text: configData?.second_media_text,
            subText: configData?.second_media_sub_text,
        },
        {
            image: configData?.third_media_path,
            text: configData?.third_media_text,
            subText: configData?.third_media_sub_text,
        },
    ];


    const openPopup = () => setIsPopupVisible(true);

    const closePopup = (wasAgreed = false) => {
        setIsPopupVisible(false);
        setIsProcessing(false);

        setTimeout(() => {
            const onAgreementHandler = resolvePopupCallback(onAgreement);
            if (wasAgreed && onAgreementHandler) {
                setTimeout(onAgreementHandler, CLOSE_TIMEOUT);
            }
            handleClose();
        }, CLOSE_TIMEOUT);
    };

    const closePopupWithoutApproval = () => {
        // Prevent closing the popup while waiting for a response
        if (isProcessing) return;
        else setIsProcessing(true);
        dispatch(updateUserDataSharingConsent({
            isApproved: false,
            approveSource: null,
        }, closePopup));
    };

    const handleBackdropClick = ({target}) => {
        // Close popup only if backdrop was clicked, not its children
        if (target.classList.contains('backdrop')) {
            closePopupWithoutApproval();
        }
    };

    const handleLinkSubsidyClick = (event) =>{
        event.preventDefault();
        event.stopPropagation()

        setShowSubsidyLinkNotClickedError(false)

        const url = event.currentTarget.href
        setLinkSubsidyClicked(true)
        dispatch(Actions.addPopup({
            type: Popups.IFRAME_POPUP,
            iframeUrl: url
        }))
    }

    const handleLinkDataClick = (event) =>{
        event.preventDefault();
        event.stopPropagation()

        setShowDataLinkNotClickedError(false)

        const url = event.currentTarget.href
        setLinkDataClicked(true)
        dispatch(Actions.addPopup({
            type: Popups.IFRAME_POPUP,
            iframeUrl: url
        }))
    }

    const handleLinkMarketingClick = (event) =>{
        event.preventDefault();
        event.stopPropagation()

        setShowMarketingLinkNotClickedError(false)

        const url = event.currentTarget.href
        setLinkMarketingClicked(true)
        dispatch(Actions.addPopup({
            type: Popups.IFRAME_POPUP,
            iframeUrl: url
        }))
    }


    const handleDataCheckboxChange = () => {
        const newValue = !isDataSharingCheckboxChecked;

        setIsDataSharingCheckboxChecked(newValue);
        setIsShowCheckboxError(!newValue);
    }

    const handleSubmitButtonClick = () => {
        setIsClickedSubmit(true)
        setTimeout(() => setIsClickedSubmit(false), 2000);

        let isHasError = false

        if (isShowDataSharingCheckbox) {
            if (!linkDataClicked) {
                isHasError = true;
                setShowDataLinkNotClickedError(true)
            }
            else if (!isDataSharingCheckboxChecked) {
                isHasError = true
                setIsShowCheckboxError(true)
            }
        }


        if (isShowMarketingCheckbox) {
            if (!linkMarketingClicked) {
                isHasError = true;
                setShowMarketingLinkNotClickedError(true)
            }
            else if (!isApprovedMarketing) {
                isHasError = true
                setIsShowMarketingCheckboxError(true)
            }
        }


        if (isShowSubsidyCheckbox) {
            if (!linkSubsidyClicked) {
                isHasError = true;
                setShowSubsidyLinkNotClickedError(true)
            }
            else if (!isApprovedSubsidy) {
                isHasError = true
                setIsShowSubsidyCheckboxError(true)
            }
        }

        if (isHasError) {
            return
        }

        if (typeof window.fbq === 'function') {
            if (isShowSubsidyCheckbox && isShowMarketingCheckbox)
                window.fbq('track', 'click_purchase');
        }

        dispatch(updateUserDataSharingAndMarketingConsent({
            isApproved: true,
            benefitId,
            approveSource: SOURCE_OF_APPROVAL,
            isUpdateDataSharing: isShowDataSharingCheckbox,
            isUpdateMarketingApproval: !!isShowMarketingCheckbox,
            isUpdateSubsidyApproval: !!isShowSubsidyCheckbox,
        }, (data) => {
            dispatch(setIsConsentsUpdateSuccessful(true))
            dispatch(setIsUpdatedConsents(true))
            dispatch(updateUserData({
                consentedToDataSharing: true,
            }))
            dispatch(setIsMarketingApproved(!!isShowMarketingCheckbox));

            closePopup(true);
        }, e => {
            dispatch(Actions.addPopup({
                type: Popups.GENERAL_MESSAGE,
                text: translations.general_error_message,
            }));
            setIsProcessing(false);
        }))
    };

    useLayoutEffect(openPopup, []);


    const handleMarketingCheckboxChange = (e) => {
        const value = e
        setIsApprovedMarketing(value)

        setIsShowMarketingCheckboxError(!value)
    }

    const handleSubsidyCheckboxChange = (e) => {
        const value = e
        setIsApprovedSubsidy(value)

        setIsShowSubsidyCheckboxError(!value)
    }

    return configData ? (
        <div
            className={combineClassNames(
                'backdrop',
                isPopupVisible && 'active',
            )}
            onClick={handleBackdropClick}
        >
            <div className="data-sharing-popup">
                <div className="popup-close-container">
                    <button
                        type="button"
                        className="popup-close-button"
                        onClick={closePopupWithoutApproval}
                        aria-label="Close Popup"
                    >
                        <img src={closeIconPath} alt="Close Icon" className="popup-close-button-icon"/>
                    </button>
                </div>

                <div className="popup-blue-container">
                    {/*<div className="blue-container-top">*/}
                    {/*    <div className="blue-container-top-shadow"/>*/}
                    {/*</div>*/}

                    <div className="blue-container-content">
                        <h1 className="popup-title">
                            {headerText}
                            {isMobile ? <br/> : ' '}
                            <div className='popup-subtitle'>{subtitle}</div>
                        </h1>

                        <ul className="popup-list">
                            {listItems.map((singleItem, itemIndex) => (
                                <li key={itemIndex} className="list-item">
                                    <div className="item-image-container">
                                        <img
                                            src={getMediaPath(singleItem['image'])}
                                            alt={singleItem['text']}
                                            className="item-image"
                                        />
                                    </div>

                                    <div className='item-text-container'>
                                        <h2 className="item-text">
                                            {singleItem['text']}
                                        </h2>

                                        <p className="item-sub-text">
                                            {singleItem['subText']}
                                        </p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/*<div className="blue-container-bottom"/>*/}
                </div>

                <div className="popup-grey-container">
                    <div className="grey-container-content">
                        <div className="inner-text-container">
                            {isShowDataSharingCheckbox &&
                                <>
                                    <HtmlCheckbox
                                        name="data-share-agreement"
                                        value={isDataSharingCheckboxChecked}
                                        html={dataSharingCheckboxHtml}
                                        onChange={handleDataCheckboxChange}
                                        handleLinkClick={handleLinkDataClick}
                                        disableCheckbox={!linkDataClicked}
                                        onDisabledClicked={() => {
                                            setShowDataLinkNotClickedError(true)
                                        }}
                                        checked_img={checkedCheckboxImagePath}
                                        not_checked_img={emptyCheckboxImagePath}
                                        className="popup-checkbox"
                                    />
                                    <div className="popup-checkbox-error">
                                        {showDataLinkNotClickedError ? translations['agreement_link_not_clicked_error'] : hasDataCheckboxError ? translations['agreement_not_checked_error'] : null}
                                    </div>
                                </>
                            }

                            {isShowMarketingCheckbox &&
                                <div style={{marginTop: 15}}>
                                    <HtmlCheckbox
                                        name="data-share-agreement"
                                        html={marketingCheckboxHtml}
                                        value={isApprovedMarketing}
                                        onChange={handleMarketingCheckboxChange}
                                        handleLinkClick={handleLinkMarketingClick}
                                        disableCheckbox={!linkMarketingClicked}
                                        onDisabledClicked={() => {
                                            setShowMarketingLinkNotClickedError(true)
                                        }}
                                        checked_img={checkedCheckboxImagePath}
                                        not_checked_img={emptyCheckboxImagePath}
                                        className="popup-checkbox"
                                    />
                                    <div className="popup-checkbox-error">
                                        {showMarketingLinkNotClickedError ? translations['agreement_link_not_clicked_error'] : isShowMarketingCheckboxError ? translations['agreement_not_checked_error'] : null}
                                    </div>
                                </div>}

                            {isShowSubsidyCheckbox &&
                                <div style={{marginTop: 10}}>
                                    <HtmlCheckbox
                                        name="data-share-agreement"
                                        html={subsidyCheckboxHtml}
                                        value={isApprovedSubsidy}
                                        disableCheckbox={!linkSubsidyClicked}
                                        handleLinkClick={handleLinkSubsidyClick}
                                        onChange={handleSubsidyCheckboxChange}
                                        onDisabledClicked={() => {
                                            setShowSubsidyLinkNotClickedError(true)
                                        }}
                                        checked_img={checkedCheckboxImagePath}
                                        not_checked_img={emptyCheckboxImagePath}
                                        className="popup-checkbox"
                                    />
                                    <div className="popup-checkbox-error">
                                        {showSubsidyLinkNotClickedError ? translations['agreement_link_not_clicked_error'] : isShowSubsidyCheckboxError ? translations['agreement_not_checked_error'] : null}
                                    </div>
                                </div>}
                        </div>

                        <button
                            disabled={isClickedSubmit}
                            type="button"
                            className={combineClassNames(
                                'submit-button',
                                isProcessing && 'submit-button_disabled',
                            )}
                            onClick={handleSubmitButtonClick}
                        >
                            {configData['agreement_button']}
                        </button>
                    </div>
                    <div className="grey-container-bottom"/>
                </div>
            </div>
        </div>
    ) : null;
};

export default UsersConsentsPopup;
